'use client'

import { useParams, useRouter, useSearchParams } from 'next/navigation'
import type { MouseEvent } from 'react'
import { useForm } from 'react-hook-form'
import { useStore } from '@/store'
import {
  useProductQuestionQuery,
  useCreateProductQuestionMutation,
} from '@/generated/graphql'
import { ContentWrapper } from '@/General/ContentWrapper'
import { H1 } from '@/General/H1'
import { Breadcrumb } from '@/General/Breadcrumb'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { Button } from '@/General/Button'
import { LoadingSpinner } from '@/General/LoadingSpinner'
import { TextLink } from '@/General/TextLink'
import { InfoAlert } from '@/General/InfoAlert'
import { CommunityRulesAlert } from '../CommunityRules'
import { useQueryClient } from '@tanstack/react-query'
import { toastEmitter } from '@/General/Toast'
import { LoginModal } from '@/MainLayout/Header/TopMenu/LoginModal'
import { useState } from 'react'
import {
  ANSWER_MIN_LENGTH,
  ANSWER_MAX_LENGTH,
  ERROR_MESSAGES,
  CACHE_KEYS,
} from '@/communityQuestions'
import { parseProductQuestionId } from './parseProductQuestionId'

interface AnswerFormData {
  answer: string
}

export default function AnswerQuestionPage() {
  const params = useParams<{ productSlug: string; questionId: string }>()
  const router = useRouter()
  const searchParams = useSearchParams()
  const user = useStore(state => state.user)
  const queryClient = useQueryClient()
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const productQuestionId = parseProductQuestionId(params.questionId)
  const returnUrl =
    searchParams.get('returnUrl') || `/product/${params.productSlug}`
  const search = searchParams.toString()
  const redirectPath = search
    ? `/product/${params.productSlug}/communityquestion/${params.questionId}?${search}`
    : `/product/${params.productSlug}/communityquestion/${params.questionId}`
  const signupUrl = `/account/signup${
    redirectPath ? `?redirect=${encodeURIComponent(redirectPath)}` : ''
  }`
  const handleLoginClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    setIsLoginOpen(true)
  }

  const buildSuccessUrl = (baseUrl: string) => {
    const [path, search] = baseUrl.split('?')
    const params = new URLSearchParams(search || '')
    params.set('answerPosted', 'true')
    return `${path}?${params.toString()}`
  }

  const { data: productData, isLoading: isProductLoading } =
    useProductQuestionQuery(
      {
        productQuestionId: productQuestionId ?? 0,
      },
      {
        enabled: productQuestionId !== null && !!params.productSlug,
      }
    )

  const createQuestionMutation = useCreateProductQuestionMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.PRODUCT, { slug: params.productSlug }],
      })
      queryClient.invalidateQueries({
        queryKey: [
          CACHE_KEYS.PRODUCT_QUESTIONS_INFINITE,
          { productId: productData?.productQuestion?.productId || 0 },
        ],
      })

      // Show success toast
      toastEmitter.emit('toast', {
        title: 'Answer posted!',
        body: 'Your answer has been successfully posted to the community.',
        variant: 'success',
        duration: 5000,
      })
      // Return to the product page and show success message
      router.replace(buildSuccessUrl(returnUrl))
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<AnswerFormData>()

  const pages = [
    {
      name: productData?.productQuestion?.productName || '',
      href: `/product/${params.productSlug}`,
    },
    { name: 'Answer Question', href: '#' },
  ]

  const onSubmit = async (data: AnswerFormData) => {
    try {
      await createQuestionMutation.mutateAsync({
        productId: productData?.productQuestion?.productId || 0,
        question: data.answer,
        responseTo: productQuestionId,
      })
    } catch (error: any) {
      if (error?.message?.includes('inappropriate language')) {
        setError('answer', {
          type: 'manual',
          message: ERROR_MESSAGES.PROFANITY_DETECTED,
        })
      } else {
        setError('root', {
          type: 'manual',
          message: ERROR_MESSAGES.SUBMISSION_FAILED,
        })
      }
    }
  }

  if (isProductLoading) {
    return (
      <ContentWrapper>
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </ContentWrapper>
    )
  }

  if (!productData?.productQuestion) {
    return (
      <ContentWrapper>
        <H1>Question Not Found</H1>
        <Breadcrumb pages={pages} />
        <div className="text-gray-500 italic">
          The question you're looking for doesn't exist or has been removed.
        </div>
      </ContentWrapper>
    )
  }

  return (
    <ContentWrapper>
      <H1>
        Answer community question about{' '}
        {productData?.productQuestion?.productName}
      </H1>
      <Breadcrumb pages={pages} />

      {!user ? (
        <>
          <LoginModal
            isLoginOpen={isLoginOpen}
            setIsLoginOpen={setIsLoginOpen}
            redirectTo={redirectPath}
          />
          <InfoAlert
            variant="warning"
            title="Please log in to answer a question"
            className="mt-4"
          >
            You need to be logged in to answer community questions.{' '}
            <TextLink href={redirectPath} onClick={handleLoginClick}>
              Click here to log in
            </TextLink>{' '}
            or <TextLink href={signupUrl}>create an account</TextLink> if you
            don't have one yet.
          </InfoAlert>
        </>
      ) : (
        <>
          <p className="text-lg my-4">
            Help out a fellow magician by answering their question about{' '}
            <TextLink href={`/product/${params.productSlug}`}>
              {productData?.productQuestion?.productName}
            </TextLink>
            . Share your knowledge and experience with the community!
          </p>

          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold mb-2">
              {productData?.productQuestion?.customerName} asks:
            </p>
            <p className="text-lg">{productData?.productQuestion?.question}</p>
          </div>

          <CommunityRulesAlert />

          <div>
            <ErrorBox
              errors={
                Object.values(errors)
                  .map(error => error?.message)
                  .filter(Boolean) as string[]
              }
            />

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label
                  htmlFor="answer"
                  className="block font-medium text-gray-700 mb-2"
                >
                  Your answer:
                </label>
                <div className="mt-1">
                  <textarea
                    id="answer"
                    rows={6}
                    className="shadow-sm block w-full border-gray-300 rounded-md resize-vertical"
                    {...register('answer', {
                      required: ERROR_MESSAGES.ANSWER_REQUIRED,
                      minLength: {
                        value: ANSWER_MIN_LENGTH,
                        message: ERROR_MESSAGES.ANSWER_TOO_SHORT,
                      },
                      maxLength: {
                        value: ANSWER_MAX_LENGTH,
                        message: ERROR_MESSAGES.ANSWER_TOO_LONG,
                      },
                    })}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  color="green"
                  size="lg"
                  disabled={createQuestionMutation.isPending}
                >
                  {createQuestionMutation.isPending
                    ? 'Posting...'
                    : 'Post your answer'}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </ContentWrapper>
  )
}
